/**
 * Page Validation Commande (US-018)
 * E-Store - FrontOffice
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useCart, orderApi } from '@e-store/shared';
import type { OrderDeliveryMode } from '@e-store/shared';
import { AmazonHeader } from '../components/AmazonHeader';
import './CheckoutPage.css';

interface DeliveryInfo {
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string; // Obligatoire pour contacter le client
  instructions?: string;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { cart, clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>({
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    phone: user?.phone || '',
    instructions: '',
  });

  // Mode d'acheminement : la livraison reste le defaut, le retrait est un choix explicite
  const [deliveryMode, setDeliveryMode] = useState<OrderDeliveryMode>('delivery');
  const isPickup = deliveryMode === 'pickup';

  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }

    if (!cart || cart.items.length === 0) {
      navigate('/cart');
    }
  }, [isAuthenticated, cart, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Garde n°1 (JS) : l'adresse n'est exigee qu'en mode livraison
    if (!isPickup && (!deliveryInfo.address || !deliveryInfo.city || !deliveryInfo.postalCode)) {
      setError('Veuillez remplir tous les champs obligatoires de livraison');
      return;
    }

    if (!deliveryInfo.phone || deliveryInfo.phone.trim().length < 8) {
      setError('Veuillez entrer un numéro de téléphone valide pour que nous puissions vous contacter');
      return;
    }

    if (!user?.email) {
      setError('Email utilisateur manquant. Veuillez vous reconnecter.');
      return;
    }

    if (!user?.id) {
      setError('Utilisateur non identifié. Veuillez vous reconnecter.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const clientName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;

      const orderData = {
        clientId: user.id,
        clientName,
        clientEmail: user.email,
        clientPhone: deliveryInfo.phone,
        // En retrait sur place : aucun objet `delivery` envoye (le backend le traite deja
        // comme optionnel), et le mode est trace explicitement sur la commande.
        delivery: isPickup
          ? undefined
          : {
              address: deliveryInfo.address,
              city: deliveryInfo.city,
              postalCode: deliveryInfo.postalCode,
              country: deliveryInfo.country,
            },
        deliveryMode,
        notes: deliveryInfo.instructions
          ? `${notes}\n\n${isPickup ? 'Instructions pour le retrait' : 'Instructions de livraison'}: ${deliveryInfo.instructions}`.trim()
          : (notes || undefined),
      };

      const response = await orderApi.createOrder(orderData);

      if (response.error) {
        // Afficher les détails de validation si présents
        const details = response.details as Array<{ path: string[]; message: string }> | undefined;
        if (details && Array.isArray(details)) {
          const detailMessages = details.map(d => `${d.path.join('.')}: ${d.message}`).join(', ');
          setError(`${response.error}: ${detailMessages}`);
        } else {
          setError(response.error);
        }
        console.error('Order creation error:', response.error, response.details);
        return;
      }

      // Vider le panier
      await clearCart();

      // Rediriger vers la confirmation
      navigate(`/order-success?orderId=${response.data?.order.id}`);
    } catch (err: any) {
      setError('Erreur lors de la création de la commande');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (!cart || cart.items.length === 0) {
    return null;
  }

  return (
    <div className="checkout-page">
      {/* Header Amazon */}
      <AmazonHeader showSearch={true} />

      {/* Page Content */}
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Validation de la Commande</h1>
          <p className="page-description">
            Vérifiez vos informations et finalisez votre commande
          </p>
        </div>

        <Link to="/cart" className="back-link">← Retour au panier</Link>

        {/* Messages */}
        {error && (
          <div className="message error">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="checkout-form">
          {/* Left Column */}
          <div className="checkout-main">
            {/* Customer Info */}
            <div className="checkout-section">
              <h2>Informations Client</h2>
              <div className="info-display">
                <div className="info-item">
                  <span className="info-label">Nom:</span>
                  <span className="info-value">
                    {user?.firstName} {user?.lastName}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{user?.email}</span>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="checkout-section">
              <h2>{isPickup ? 'Retrait sur place' : 'Informations de Livraison'}</h2>

              {/* Choix du mode d'acheminement (radio group, pas de <select> natif) */}
              <div className="form-group">
                <span className="form-legend">Comment souhaitez-vous recevoir votre commande ? *</span>
                <div className="delivery-mode-options" role="radiogroup" aria-label="Mode de réception">
                  <label
                    className={`delivery-mode-option${!isPickup ? ' selected' : ''}`}
                    htmlFor="deliveryMode-delivery"
                  >
                    <input
                      type="radio"
                      id="deliveryMode-delivery"
                      name="deliveryMode"
                      value="delivery"
                      checked={!isPickup}
                      onChange={() => setDeliveryMode('delivery')}
                    />
                    <span className="delivery-mode-label">
                      <strong>Livraison</strong>
                      <small>Nous livrons à l'adresse de votre choix</small>
                    </span>
                  </label>

                  <label
                    className={`delivery-mode-option${isPickup ? ' selected' : ''}`}
                    htmlFor="deliveryMode-pickup"
                  >
                    <input
                      type="radio"
                      id="deliveryMode-pickup"
                      name="deliveryMode"
                      value="pickup"
                      checked={isPickup}
                      onChange={() => setDeliveryMode('pickup')}
                    />
                    <span className="delivery-mode-label">
                      <strong>Retrait sur place</strong>
                      <small>Vous venez chercher votre commande, aucune adresse requise</small>
                    </span>
                  </label>
                </div>
              </div>

              {!isPickup && (
                <>
                  <div className="form-group">
                    <label htmlFor="address">Adresse *</label>
                    <input
                      type="text"
                      id="address"
                      value={deliveryInfo.address}
                      onChange={(e) =>
                        setDeliveryInfo({ ...deliveryInfo, address: e.target.value })
                      }
                      placeholder="123 rue de la Livraison"
                      required={!isPickup}
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">Ville *</label>
                      <input
                        type="text"
                        id="city"
                        value={deliveryInfo.city}
                        onChange={(e) => setDeliveryInfo({ ...deliveryInfo, city: e.target.value })}
                        placeholder="Paris"
                        required={!isPickup}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="postalCode">Code Postal *</label>
                      <input
                        type="text"
                        id="postalCode"
                        value={deliveryInfo.postalCode}
                        onChange={(e) =>
                          setDeliveryInfo({ ...deliveryInfo, postalCode: e.target.value })
                        }
                        placeholder="75001"
                        required={!isPickup}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Pays</label>
                    <input
                      type="text"
                      id="country"
                      value={deliveryInfo.country}
                      onChange={(e) =>
                        setDeliveryInfo({ ...deliveryInfo, country: e.target.value })
                      }
                      placeholder="France"
                      className="form-input"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label htmlFor="phone">Téléphone * <small>(pour vous contacter rapidement)</small></label>
                <input
                  type="tel"
                  id="phone"
                  value={deliveryInfo.phone}
                  onChange={(e) => setDeliveryInfo({ ...deliveryInfo, phone: e.target.value })}
                  placeholder="+261 34 00 000 00"
                  required
                  minLength={8}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="instructions">
                  {isPickup ? 'Instructions pour le retrait' : 'Instructions de livraison'}
                </label>
                <textarea
                  id="instructions"
                  value={deliveryInfo.instructions}
                  onChange={(e) =>
                    setDeliveryInfo({ ...deliveryInfo, instructions: e.target.value })
                  }
                  placeholder="Digicode, étage, etc."
                  rows={3}
                  className="form-textarea"
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div className="checkout-section">
              <h2>Notes (optionnel)</h2>
              <div className="form-group">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajoutez des notes pour votre commande..."
                  rows={4}
                  className="form-textarea"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="checkout-sidebar">
            <div className="order-summary">
              <h2>Récapitulatif</h2>

              <div className="summary-items">
                {cart.items.map((item) => (
                  <div key={item.articleId} className="summary-item">
                    <div className="item-name-qty">
                      <span className="item-name">{item.articleName}</span>
                      <span className="item-qty">× {item.quantity}</span>
                    </div>
                    <span className="item-price">{formatPrice(item.unitPriceTTC * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="summary-totals">
                <div className="summary-line">
                  <span>Sous-total HT:</span>
                  <span>{formatPrice(cart.totalHT)}</span>
                </div>
                <div className="summary-line">
                  <span>TVA:</span>
                  <span>{formatPrice(cart.totalTVA)}</span>
                </div>
                <div className="summary-line summary-total">
                  <span>Total TTC:</span>
                  <span>{formatPrice(cart.totalTTC)}</span>
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-submit" disabled={loading}>
                {loading ? 'Création en cours...' : 'Valider la Commande'}
              </button>

              <div className="security-info">
                <p>🔒 Paiement sécurisé</p>
                <p className="security-text">
                  Vos informations sont protégées et cryptées
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
